import { useState, useEffect, useMemo } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import axios from "@/lib/axios";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Search, Filter, Calendar, Clock, ArrowRight, AlertCircle, Building2, Clock3, Bell, ChevronLeft, ChevronRight, X, FileX, CheckCircle2, XCircle, History, Megaphone, Archive, FileClock } from "lucide-react";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { useDebounce } from "@/hooks/useDebounce";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface Announcement {
  _id: string;
  title: string;
  status: keyof typeof statusColors;
  createdAt: string;
  [key: string]: any;
}

const statusColors = {
  initial_review: "bg-blue-100 text-blue-800 border-blue-300",
  pending_admin: "bg-purple-100 text-purple-800 border-purple-300",
  manager_review: "bg-yellow-100 text-yellow-800 border-yellow-300",
  approved: "bg-green-100 text-green-800 border-green-300",
  rejected: "bg-red-100 text-red-800 border-red-300",
};

const statusLabels = {
  initial_review: "Veeq Seslendirme Bekleniyor",
  pending_admin: "Revize Talebi",
  manager_review: "İşlem Onayı Bekliyor",
  approved: "Yayında",
  rejected: "İptal Edildi",
};

// Bir ses dosyası müşteri onayı bekliyorsa ya da revizyon isteniyorsa, anonsun
// kendi "status" alanı bunu senkron yansıtmayabilir (bkz. updateAnnouncementStatus
// akışı) - bu yüzden rozet ve renk her zaman ses dosyalarının gerçek durumuna
// göre hesaplanmalı, sadece announcement.status'e bakılmamalı
const getEffectiveStatusKey = (announcement: any): keyof typeof statusColors => {
  if (announcement.status === "rejected" || announcement.status === "approved") {
    return announcement.status;
  }
  if (announcement.audioFile?.some((audio: any) => audio.status === "manager_review")) {
    return "manager_review";
  }
  if (announcement.audioFile?.some((audio: any) => audio.status === "pending_admin")) {
    return "pending_admin";
  }
  return announcement.status;
};

// "Revize Talebi" (pending_admin) iki farklı anlama gelebilir: ya super admin
// müşteriye az önce bir not iletti (müşterinin cevabı bekleniyor), ya da
// müşteri başlık/içeriğini güncelleyip geri gönderdi (admin'in incelemesi
// bekleniyor). Anonsun kendi statusHistory'sindeki en son pending_admin
// geçişini kimin yaptığına bakarak ikisini ayırt et
const getEffectiveStatusLabel = (announcement: any): string => {
  const key = getEffectiveStatusKey(announcement);
  if (key === "pending_admin" && announcement.status === "pending_admin" && Array.isArray(announcement.statusHistory)) {
    const entries = announcement.statusHistory.filter((h: any) => h.to === "pending_admin");
    if (entries.length > 0) {
      const latest = entries.reduce((a: any, b: any) => (new Date(a.date) > new Date(b.date) ? a : b));
      if (latest.user?.role === "super_admin") {
        return "Müşteri Bekleniyor";
      }
    }
  }
  return statusLabels[key];
};

const formatDate = (date: string) => {
  const d = new Date(date);
  return d.toLocaleDateString('tr-TR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    weekday: 'long',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getTimeRemaining = (endDate: string) => {
  const end = new Date(endDate);
  const now = new Date();
  const diff = end.getTime() - now.getTime();

  if (diff <= 0) return "Süresi Doldu";

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

  if (days > 0) return `${days} gün ${hours} saat`;
  if (hours > 0) return `${hours} saat ${minutes} dk`;
  return `${minutes} dakika`;
};

// calculateStats fonksiyonu kaldırıldı - artık backend'den alıyoruz

const getTodayDateInputValue = () => new Date().toISOString().slice(0, 10);

const getOneMonthAgoDateInputValue = () => {
  const date = new Date();
  date.setMonth(date.getMonth() - 1);
  return date.toISOString().slice(0, 10);
};

export default function AnnouncementListPage() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState({
    status: "all",
    search: "",
    page: 1,
    archived: false
  });

  // İstatistik kartlarının ve listenin kapsadığı tarih aralığı - panel açıldığında
  // bugünden 1 ay öncesi ile başlar, bitişte üst sınır yok (aktif/gelecek anonslar
  // her zaman görünsün diye) - kullanıcı isterse elle bir bitiş seçebilir
  const [dateRange, setDateRange] = useState(() => ({
    start: getOneMonthAgoDateInputValue(),
    end: ""
  }));

  // Tarih aralığını API'nin anlayacağı gün başı/gün sonu ISO formatına çevir
  // (anonsun kendi bitiş tarihine göre filtreler - createdAt değil)
  const rangeParams = useMemo(() => {
    const params: { rangeStart?: string; rangeEnd?: string } = {};
    if (dateRange.start) {
      params.rangeStart = new Date(`${dateRange.start}T00:00:00`).toISOString();
    }
    if (dateRange.end) {
      params.rangeEnd = new Date(`${dateRange.end}T23:59:59.999`).toISOString();
    }
    return params;
  }, [dateRange.start, dateRange.end]);

  // Debounced search - 400ms gecikme ile API çağrısı yap
  const debouncedSearch = useDebounce(filter.search, 400);

  // localStorage'dan kayıtlı sayfa numarasını al veya 1'i kullan
  const [currentPage, setCurrentPage] = useState(() => {
    const savedPage = localStorage.getItem('announcementListPage');
    return savedPage ? parseInt(savedPage, 10) : 1;
  });
  
  // Son görüntülenen anons ID'sini takip etmek için state
  const [lastViewedId, setLastViewedId] = useState<string | null>(
    sessionStorage.getItem('lastViewedAnnouncementId')
  );
  
  // Son görüntülenen anons vurgusunu 3 saniye sonra kaldır ve görünür olmasını sağla
  useEffect(() => {
    if (lastViewedId) {
      // Vurgulanan öğeye otomatik kaydırma yap
      setTimeout(() => {
        const element = document.getElementById(`announcement-row-${lastViewedId}`);
        if (element) {
          // Smooth scroll ile öğeye git
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100); // Kısa bir gecikme ekleyerek DOM'un güncellenmesini bekle

      // Vurgulamayı 3 saniye sonra kaldır
      const timer = setTimeout(() => {
        setLastViewedId(null);
        sessionStorage.removeItem('lastViewedAnnouncementId');
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [lastViewedId]);
  const itemsPerPage = 40;

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["announcements", currentPage, filter.status, filter.archived, debouncedSearch, rangeParams.rangeStart, rangeParams.rangeEnd],
    queryFn: async () => {
      const response = await axios.get('/announcements', {
        params: {
          page: currentPage,
          limit: itemsPerPage, // Sadece 40 kayıt çek
          status: !filter.archived && filter.status !== 'all' ? filter.status : undefined,
          search: debouncedSearch || undefined,
          archived: filter.archived ? 'true' : undefined,
          ...(filter.archived ? {} : rangeParams)
        }
      });
      return response.data;
    },
    placeholderData: keepPreviousData, // Yüklenirken önceki veriyi göster
  });

  // İstatistikleri backend'den çek (seçilen tarih aralığına göre)
  const { data: statsData } = useQuery({
    queryKey: ["announcement-statistics", rangeParams.rangeStart, rangeParams.rangeEnd],
    queryFn: async () => {
      const response = await axios.get('/announcements/statistics', { params: rangeParams });
      return response.data;
    }
  });


  const announcements = data?.announcements || [];

  // Backend'den zaten filtrelenmiş ve sayfalanmış veri geliyor
  const filteredAnnouncements = announcements;
  const currentAnnouncements = announcements;
  const totalPages = data?.pagination?.totalPages || 1;
  const startIndex = 0; // Backend'den sayfalanmış veri geliyor
  const endIndex = announcements.length; // Backend'den sayfalanmış veri geliyor

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // Sayfa numarasını localStorage'a kaydet
    localStorage.setItem('announcementListPage', page.toString());
  };

  const handleRowClick = (id: string, e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('button')) {
      e.stopPropagation();
      return;
    }
    // Sayfa numarasını localStorage'a kaydet
    localStorage.setItem('announcementListPage', currentPage.toString());
    // Son görüntülenen anons ID'sini sessionStorage'a kaydet
    sessionStorage.setItem('lastViewedAnnouncementId', id);
    navigate(`/announcements/${id}`);
  };

  // Bir istatistik kartına tıklandığında listeyi o duruma/tarih aralığına göre filtrele
  const applyStatFilter = (status: string) => {
    setFilter(f => ({ ...f, status, archived: false }));
    setCurrentPage(1);
  };

  // "Arşiv" kartı - bitiş tarihi geçmiş tüm anonslar (onaylı ya da değil), tarih aralığından bağımsız
  const applyArchiveFilter = () => {
    setFilter(f => ({ ...f, status: 'all', archived: true }));
    setCurrentPage(1);
  };

  // İstatistik kartları için veri - backend'den gelen verilerle
  const statItems = useMemo(() => [
    {
      label: "Toplam Anons",
      value: statsData?.total || 0,
      icon: Bell,
      color: "text-blue-500",
      bgColor: "bg-blue-500/10",
      info: "Seçili tarih aralığındaki tüm anonslar",
      statusValue: "all",
      onClick: () => applyStatFilter("all")
    },
    {
      label: "Veeq Seslendirme Bekleniyor",
      value: statsData?.initialReview || 0,
      icon: FileClock,
      color: "text-blue-600",
      bgColor: "bg-blue-500/10",
      info: "Yeni gelen, henüz incelenmemiş anonslar",
      statusValue: "initial_review",
      onClick: () => applyStatFilter("initial_review")
    },
    {
      label: "Müşteri Onayı Bekleniyor",
      value: statsData?.pending || 0,
      icon: Clock3,
      color: "text-amber-500",
      bgColor: "bg-amber-500/10",
      info: "İşlem onayı bekleyen anonslar",
      statusValue: "manager_review",
      onClick: () => applyStatFilter("manager_review")
    },
    {
      label: "Revize Talebi",
      value: statsData?.pendingAdmin || 0,
      icon: History,
      color: "text-purple-500",
      bgColor: "bg-purple-500/10",
      info: "Düzeltilmesi istenen anonslar",
      statusValue: "pending_admin",
      onClick: () => applyStatFilter("pending_admin")
    },
    {
      label: "İptal Edilen",
      value: statsData?.rejected || 0,
      icon: XCircle,
      color: "text-red-500",
      bgColor: "bg-red-500/10",
      info: "Reddedilen/iptal edilen anonslar",
      statusValue: "rejected",
      onClick: () => applyStatFilter("rejected")
    },
    {
      label: "Yayında Olan",
      value: statsData?.approved || 0,
      icon: CheckCircle2,
      color: "text-green-600",
      bgColor: "bg-green-500/10",
      info: "Aktif yayında olan anonslar",
      statusValue: "approved",
      onClick: () => applyStatFilter("approved")
    },
    {
      label: "Arşiv",
      value: statsData?.archived || 0,
      icon: Archive,
      color: "text-slate-500",
      bgColor: "bg-slate-500/10",
      info: "Bitiş tarihi geçmiş tüm anonslar",
      statusValue: "archived",
      onClick: applyArchiveFilter
    },
  ], [statsData]);

  if (isLoading) {
    return (
      <div className="h-full flex-1">
        <div className="flex-1 space-y-6 p-8 pt-6">
          {/* Hero Section Skeleton */}
          <div className="rounded-2xl overflow-hidden shadow-lg">
            <div className="h-56 bg-gradient-to-r from-cyan-600 to-cyan-400 p-8">
              <div className="flex justify-between items-start relative w-full h-full">
                <div className="space-y-3 flex-1">
                  <Skeleton className="h-4 w-40 bg-white/20" />
                  <Skeleton className="h-9 w-80 bg-white/30" />
                  <Skeleton className="h-16 w-[500px] bg-white/20" />
                </div>
                <Skeleton className="w-40 h-40 rounded-lg bg-white/10" />
              </div>
            </div>
          </div>

          {/* İstatistik Kartları Skeleton - Gerçek kart boyutlarında ve dekorasyonlarıyla */}
          <div className="grid gap-4 grid-cols-2 md:grid-cols-4 xl:grid-cols-7">
            {Array.from({ length: 7 }).map((_, index) => (
              <Card key={index} className="overflow-hidden border border-slate-200 shadow-sm hover:shadow-md transition-all duration-200 hover:border-slate-300 bg-white rounded-xl">
                <CardContent className="p-0">
                  <div className="relative">
                    {/* Arka plan dekoratif gradient */}
                    <div className="absolute top-0 right-0 w-32 h-32 opacity-5 transform translate-x-12 -translate-y-12 rounded-full bg-slate-200 blur-2xl"></div>

                    <div className="p-6 relative">
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex-1">
                          <Skeleton className="h-4 w-[120px] mb-2" />
                          <Skeleton className="h-9 w-[70px]" />
                        </div>
                        <Skeleton className="h-14 w-14 rounded-xl" />
                      </div>

                      {/* Alt bilgi çubuğu */}
                      <div className="pt-3 border-t border-slate-100">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <Skeleton className="w-2 h-2 rounded-full mr-2" />
                            <Skeleton className="h-4 w-[120px]" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Filtreler Skeleton - Gerçek filtreleme alanı boyutunda */}
          <Card>
            <CardContent className="p-6">
              <div className="space-y-2">
                <div className="flex gap-4 items-center">
                  <div className="relative flex-1">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4">
                      <Skeleton className="h-4 w-4" />
                    </div>
                    <Skeleton className="h-10 w-full" />
                  </div>
                  <Skeleton className="h-10 w-[120px]" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Anons Tablosu Skeleton - Gerçek tablo yapısında */}
          <div className="rounded-lg border overflow-hidden bg-white shadow">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-100 hover:bg-slate-100">
                  <TableHead className="py-3.5 w-[280px]">
                    <Skeleton className="h-5 w-[140px]" />
                  </TableHead>
                  <TableHead className="py-3.5 w-[180px]">
                    <Skeleton className="h-5 w-[100px]" />
                  </TableHead>
                  <TableHead className="py-3.5 w-[180px]">
                    <Skeleton className="h-5 w-[100px]" />
                  </TableHead>
                  <TableHead className="py-3.5 w-[180px]">
                    <Skeleton className="h-5 w-[100px]" />
                  </TableHead>
                  <TableHead className="py-3.5 text-right pr-4 w-[100px]">
                    <Skeleton className="h-5 w-[80px] ml-auto" />
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {Array.from({ length: 6 }).map((_, index) => (
                  <TableRow key={index} className="hover:bg-slate-50 cursor-pointer border-t">
                    <TableCell className="py-3.5">
                      <div className="space-y-1.5">
                        {/* Firma bilgisi */}
                        <div className="flex items-center gap-1.5 text-xs mb-1">
                          <Skeleton className="h-3.5 w-3.5" />
                          <Skeleton className="h-3.5 w-[100px]" />
                        </div>
                        <div>
                          <Skeleton className="h-5 w-[90%]" />
                        </div>
                        <Skeleton className="h-4 w-[80%]" />
                        <div className="flex items-center gap-1 text-xs">
                          <Skeleton className="h-3.5 w-3.5" />
                          <Skeleton className="h-3.5 w-[60px]" />
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="py-3.5">
                      <div className="space-y-2">
                        <Skeleton className="h-7 w-[90px] rounded-full" />
                        <div className="flex items-center gap-1">
                          <Skeleton className="h-4 w-4" />
                          <Skeleton className="h-4 w-[60px]" />
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="py-3.5">
                      <div className="space-y-1">
                        <div className="flex items-center gap-1.5">
                          <Skeleton className="h-4 w-4" />
                          <Skeleton className="h-5 w-[100px]" />
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Skeleton className="h-4 w-4" />
                          <Skeleton className="h-5 w-[80px]" />
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="py-3.5">
                      <div className="space-y-1">
                        <div className="flex items-center gap-1">
                          <Skeleton className="h-5 w-5 rounded-full" />
                          <Skeleton className="h-5 w-[70px]" />
                        </div>
                        <div className="flex items-center gap-1 mt-2">
                          <Skeleton className="h-4 w-4" />
                          <Skeleton className="h-4 w-[50px]" />
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-right py-3.5">
                      <Skeleton className="h-5 w-[80px] ml-auto" />
                      {/* Buton kaldırıldı */}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Sayfalama Skeleton - Gerçek sayfalama boyutlarında */}
          <div className="flex items-center justify-between mt-4 pb-4">
            <Skeleton className="h-6 w-[200px]" />
            <div className="flex items-center gap-1">
              <div className="flex items-center">
                <Skeleton className="h-10 w-10 rounded" />
                <Skeleton className="h-5 w-[70px] mx-2" />
              </div>
              <div className="flex gap-1">
                {Array.from({ length: 3 }).map((_, index) => (
                  <Skeleton key={index} className="h-10 w-10 rounded" />
                ))}
              </div>
              <div className="flex items-center">
                <Skeleton className="h-5 w-[70px] mx-2" />
                <Skeleton className="h-10 w-10 rounded" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex-1">
      <div className="flex-1 space-y-6 p-8 pt-6">
        {/* Hero Section */}
        <div className="rounded-2xl overflow-hidden shadow-lg">
          <div
            className="flex"
            style={{
              background: 'linear-gradient(to right, #0891b2, #06b6d4)',
            }}
          >
            <div className="p-8 flex justify-between items-start relative w-full">
              <div className="space-y-3 flex-1">
                <div className="text-white/80 text-sm font-medium">
                  Anons Yönetim Paneli
                </div>
                <h1 className="text-3xl font-bold text-white">
                  Anons İnceleme ve Onay Sistemi
                </h1>
                <p className="text-white/90 text-sm max-w-xl leading-relaxed">
                  Firmalardan gelen tüm anons taleplerini görüntüleyin, inceleyin ve
                  onaylayın. Anons durumlarını takip edin, zamanlama ayarlarını kontrol
                  edin ve hedef cihazları yönetin.
                </p>
              </div>
              <div
                className="w-40 h-40 rounded-lg overflow-hidden relative z-10 flex items-center justify-center ml-8"
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                }}
              >
                <Megaphone className="w-20 h-20 text-white" strokeWidth={1.5} />
              </div>
            </div>
          </div>
        </div>

        {/* Tarih Aralığı Seçici - istatistik kartlarının kapsadığı aralığı belirler */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 flex flex-wrap items-center gap-3">
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Tarih Aralığı</span>
          <Input
            type="date"
            value={dateRange.start}
            onChange={(e) => setDateRange(r => ({ ...r, start: e.target.value }))}
            className="h-9 w-auto border-slate-200"
          />
          <span className="text-slate-400 text-sm">—</span>
          <Input
            type="date"
            value={dateRange.end}
            onChange={(e) => setDateRange(r => ({ ...r, end: e.target.value }))}
            className="h-9 w-auto border-slate-200"
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-9"
            onClick={() => {
              const todayStr = getTodayDateInputValue();
              setDateRange({ start: todayStr, end: todayStr });
            }}
          >
            Bugün
          </Button>
        </div>

        {/* İstatistik Kartları */}
        <div className="grid gap-4 grid-cols-2 md:grid-cols-4 xl:grid-cols-7">
          {statItems.map((item) => (
            <Card
              key={item.label}
              onClick={item.onClick}
              className={cn(
                "overflow-hidden border shadow-sm hover:shadow-md transition-all duration-200 bg-white rounded-xl cursor-pointer",
                (item.statusValue === "archived" ? filter.archived : !filter.archived && filter.status === item.statusValue)
                  ? "border-blue-400 ring-1 ring-blue-200"
                  : "border-slate-200 hover:border-slate-300"
              )}
            >
              <CardContent className="p-0">
                <div className="relative">
                  {/* Arka plan dekoratif gradient */}
                  <div className={`absolute top-0 right-0 w-32 h-32 opacity-5 transform translate-x-12 -translate-y-12 rounded-full ${item.bgColor} blur-2xl`}></div>

                  <div className="p-4 relative">
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5 leading-tight line-clamp-2">{item.label}</p>
                        <h3 className="text-2xl font-bold text-slate-900 tracking-tight">{item.value}</h3>
                      </div>
                      <div className={cn("p-2.5 rounded-xl shadow-sm shrink-0", item.bgColor)}>
                        <item.icon className={cn("w-4 h-4", item.color)} strokeWidth={2.5} />
                      </div>
                    </div>

                    {/* Alt bilgi çubuğu */}
                    <div className="pt-2.5 border-t border-slate-100">
                      <div className="text-[11px] text-slate-600 leading-tight line-clamp-2">
                        {item.info}
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
          <div className="space-y-4">
            <div className="flex gap-3 items-center">
              <div className="relative flex-1">
                {isFetching && filter.search ? (
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5">
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-slate-300 border-t-blue-500"></div>
                  </div>
                ) : (
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                )}
                <Input
                  placeholder="ID, başlık veya içerikte ara..."
                  value={filter.search}
                  onChange={(e) => setFilter(f => ({ ...f, search: e.target.value }))}
                  className="pl-12 h-11 border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    className={`h-11 px-4 rounded-lg border-slate-200 ${filter.status !== "all" ? "border-blue-500 text-blue-600 bg-blue-50" : "hover:bg-slate-50"}`}
                  >
                    <Filter className="mr-2 h-4 w-4" />
                    Filtrele
                  </Button>
                </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    {/* Durum Filtreleri */}
                    <DropdownMenuItem 
                      onClick={() => setFilter(f => ({ ...f, status: f.status === 'initial_review' ? 'all' : 'initial_review', archived: false }))}
                      className="cursor-pointer py-2"
                    >
                      <div className="flex items-center w-full">
                        <div className={`mr-2 h-2 w-2 rounded-full ${filter.status === 'initial_review' ? 'bg-blue-500' : 'bg-slate-300'}`} />
                        <span className={filter.status === 'initial_review' ? 'font-medium' : ''}>Yeni</span>
                      </div>
                    </DropdownMenuItem>
                    
                    <DropdownMenuItem 
                      onClick={() => setFilter(f => ({ ...f, status: f.status === 'pending_admin' ? 'all' : 'pending_admin', archived: false }))}
                      className="cursor-pointer py-2"
                    >
                      <div className="flex items-center w-full">
                        <div className={`mr-2 h-2 w-2 rounded-full ${filter.status === 'pending_admin' ? 'bg-purple-500' : 'bg-slate-300'}`} />
                        <span className={filter.status === 'pending_admin' ? 'font-medium' : ''}>Revize Bekliyor</span>
                      </div>
                    </DropdownMenuItem>
                    
                    <DropdownMenuItem 
                      onClick={() => setFilter(f => ({ ...f, status: f.status === 'manager_review' ? 'all' : 'manager_review', archived: false }))}
                      className="cursor-pointer py-2"
                    >
                      <div className="flex items-center w-full">
                        <div className={`mr-2 h-2 w-2 rounded-full ${filter.status === 'manager_review' ? 'bg-yellow-500' : 'bg-slate-300'}`} />
                        <span className={filter.status === 'manager_review' ? 'font-medium' : ''}>İnceleniyor</span>
                      </div>
                    </DropdownMenuItem>
                    
                    <DropdownMenuItem 
                      onClick={() => setFilter(f => ({ ...f, status: f.status === 'approved' ? 'all' : 'approved', archived: false }))}
                      className="cursor-pointer py-2"
                    >
                      <div className="flex items-center w-full">
                        <div className={`mr-2 h-2 w-2 rounded-full ${filter.status === 'approved' ? 'bg-green-500' : 'bg-slate-300'}`} />
                        <span className={filter.status === 'approved' ? 'font-medium' : ''}>Yayında</span>
                      </div>
                    </DropdownMenuItem>
                    
                    <DropdownMenuItem 
                      onClick={() => setFilter(f => ({ ...f, status: f.status === 'rejected' ? 'all' : 'rejected', archived: false }))}
                      className="cursor-pointer py-2"
                    >
                      <div className="flex items-center w-full">
                        <div className={`mr-2 h-2 w-2 rounded-full ${filter.status === 'rejected' ? 'bg-red-500' : 'bg-slate-300'}`} />
                        <span className={filter.status === 'rejected' ? 'font-medium' : ''}>İptal Edildi</span>
                      </div>
                    </DropdownMenuItem>
                    
                    {filter.status !== 'all' && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => setFilter(f => ({ ...f, status: 'all' }))}
                          className="cursor-pointer py-2"
                        >
                          <div className="flex items-center w-full text-red-500">
                            <X className="mr-2 h-4 w-4" />
                            <span>Filtreleri Temizle</span>
                          </div>
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            
              {/* Aktif filtre belirteçleri */}
              {filter.status !== 'all' && (
                <div className="flex flex-wrap gap-2 mt-1">
                  {filter.status === 'initial_review' && (
                    <Badge variant="outline" className="flex items-center gap-1 px-3 py-1 bg-blue-50 text-blue-700 border-blue-200">
                      <div className="h-1.5 w-1.5 rounded-full bg-blue-500 mr-1"></div>
                      Yeni
                      <X 
                        className="h-3 w-3 ml-1 cursor-pointer" 
                        onClick={() => setFilter(f => ({ ...f, status: 'all' }))}
                      />
                    </Badge>
                  )}
                  
                  {filter.status === 'pending_admin' && (
                    <Badge variant="outline" className="flex items-center gap-1 px-3 py-1 bg-purple-50 text-purple-700 border-purple-200">
                      <div className="h-1.5 w-1.5 rounded-full bg-purple-500 mr-1"></div>
                      Revize Bekliyor
                      <X 
                        className="h-3 w-3 ml-1 cursor-pointer" 
                        onClick={() => setFilter(f => ({ ...f, status: 'all' }))}
                      />
                    </Badge>
                  )}
                  
                  {filter.status === 'manager_review' && (
                    <Badge variant="outline" className="flex items-center gap-1 px-3 py-1 bg-yellow-50 text-yellow-700 border-yellow-200">
                      <div className="h-1.5 w-1.5 rounded-full bg-yellow-500 mr-1"></div>
                      İnceleniyor
                      <X 
                        className="h-3 w-3 ml-1 cursor-pointer" 
                        onClick={() => setFilter(f => ({ ...f, status: 'all' }))}
                      />
                    </Badge>
                  )}
                  
                  {filter.status === 'approved' && (
                    <Badge variant="outline" className="flex items-center gap-1 px-3 py-1 bg-green-50 text-green-700 border-green-200">
                      <div className="h-1.5 w-1.5 rounded-full bg-green-500 mr-1"></div>
                      Yayında
                      <X 
                        className="h-3 w-3 ml-1 cursor-pointer" 
                        onClick={() => setFilter(f => ({ ...f, status: 'all' }))}
                      />
                    </Badge>
                  )}
                  
                  {filter.status === 'rejected' && (
                    <Badge variant="outline" className="flex items-center gap-1 px-3 py-1 bg-red-50 text-red-700 border-red-200">
                      <div className="h-1.5 w-1.5 rounded-full bg-red-500 mr-1"></div>
                      İptal Edildi
                      <X 
                        className="h-3 w-3 ml-1 cursor-pointer" 
                        onClick={() => setFilter(f => ({ ...f, status: 'all' }))}
                      />
                    </Badge>
                  )}
                </div>
              )}
            </div>
          </div>

        {/* Anons Tablosu */}
        <div className="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
          <Table>
            <TableHeader>
              <TableRow className="bg-gradient-to-r from-slate-50 to-slate-100 hover:from-slate-50 hover:to-slate-100 border-b border-slate-200">
                <TableHead className="py-4 text-slate-700 font-bold text-sm w-[280px]">Anons Detayları</TableHead>
                <TableHead className="py-4 text-slate-700 font-bold text-sm w-[180px]">Durum</TableHead>
                <TableHead className="py-4 text-slate-700 font-bold text-sm w-[180px]">Zamanlama</TableHead>
                <TableHead className="py-4 text-slate-700 font-bold text-sm w-[180px]">Cihazlar</TableHead>
                <TableHead className="py-4 text-slate-700 font-bold text-sm text-right pr-6 w-[100px]">Son Güncelleme</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={5} className="h-24 text-center">
                    <div className="p-6 space-y-6">
                      {Array.from({ length: 3 }).map((_, index) => (
                        <div key={index} className="space-y-2">
                          <Skeleton className="h-4 w-1/4" />
                          <Skeleton className="h-4 w-3/4" />
                          <Skeleton className="h-4 w-2/4" />
                        </div>
                      ))}
                    </div>
                  </TableCell>
                </TableRow>
              ) : filteredAnnouncements.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="h-24 text-center">
                    <div className="flex flex-col items-center justify-center text-slate-500">
                      <FileX className="h-10 w-10 mb-2 text-slate-400" />
                      <p>Anons bulunamadı.</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                currentAnnouncements.map((announcement: Announcement) => (
                  <TableRow
                    key={announcement._id}
                    id={`announcement-row-${announcement._id}`}
                    onClick={(e) => handleRowClick(announcement._id, e)}
                    className={cn(
                      "border-b border-slate-100 hover:bg-blue-50/30 transition-colors cursor-pointer",
                      lastViewedId === announcement._id && "bg-blue-50 transition-colors duration-300"
                    )}
                  >
                    <TableCell className="py-4">
                      <div className="space-y-1.5">
                        {/* Firma bilgisi */}
                        {announcement.createdBy?.company?.name && (
                          <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
                            <Building2 className="h-3.5 w-3.5" />
                            {announcement.createdBy.company.name}
                          </div>
                        )}
                        <div>
                          <h3 className="font-semibold text-slate-800 text-sm">{announcement.title}</h3>
                        </div>
                        <p className="text-sm text-muted-foreground line-clamp-1">
                          {announcement.content}
                        </p>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Calendar className="h-3.5 w-3.5" />
                          <span>{formatDate(announcement.startDate)}</span>
                          <ArrowRight className="h-3.5 w-3.5 mx-1" />
                          <span>{formatDate(announcement.endDate)}</span>
                        </div>
                      </div>
                    </TableCell>

                    <TableCell className="py-4">
                      <div className="space-y-2">
                        <Badge className={`${statusColors[getEffectiveStatusKey(announcement)]} border px-2.5 py-1 rounded-lg font-semibold`}>
                          {getEffectiveStatusLabel(announcement)}
                        </Badge>
                        {(announcement.status === "pending_admin" || announcement.status === "manager_review") && announcement.statusHistory?.length > 0 && (
                          <div className="text-xs text-muted-foreground mt-1.5 flex items-center gap-1">
                            <AlertCircle className="h-3.5 w-3.5" />
                            {(() => {
                              const lastStatus = announcement.statusHistory[announcement.statusHistory.length - 1];
                              if (!lastStatus?.date) return 'Az önce';
                              
                              const timeDiff = Date.now() - new Date(lastStatus.date).getTime();
                              const minutes = Math.floor(timeDiff / 60000);
                              const hours = Math.floor(minutes / 60);
                              const days = Math.floor(hours / 24);

                              if (minutes < 1) return 'Az önce';
                              if (minutes < 60) return `${minutes} dakika önce`;
                              if (hours < 24) return `${hours} saat önce`;
                              return `${days} gün önce`;
                            })()}
                          </div>
                        )}
                      </div>
                    </TableCell>

                    <TableCell className="py-4">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-slate-700">
                          <Clock className="h-3.5 w-3.5" />
                          <span className="font-medium text-sm">
                            {announcement.scheduleType === 'songs'
                              ? `Her ${announcement.songInterval} şarkıda bir`
                              : `Her ${announcement.minuteInterval} dakikada bir`}
                          </span>
                        </div>
                        <div className="text-xs text-muted-foreground flex items-center gap-1">
                          <Calendar className="h-3.5 w-3.5" />
                          Kalan süre: {getTimeRemaining(announcement.endDate)}
                        </div>
                      </div>
                    </TableCell>

                    <TableCell className="py-4">
                      <div className="space-y-2">
                        <div className="flex flex-wrap items-center gap-2">
                          {announcement.targetDevices.slice(0, 1).map((device: any) => (
                            <Badge 
                              key={device._id}
                              variant="outline" 
                              className={cn(
                                "whitespace-nowrap",
                                device.isOnline ? "bg-green-500/10 text-green-500" : ""
                              )}
                            >
                              {device.name}
                            </Badge>
                          ))}
                          
                          {announcement.targetDevices.length > 1 && (
                            <Badge variant="secondary" className="whitespace-nowrap">
                              +{announcement.targetDevices.length - 1} cihaz
                            </Badge>
                          )}

                          <span className="text-xs text-muted-foreground">
                            ({announcement.targetDevices.length} cihaz)
                          </span>
                        </div>
                      </div>
                    </TableCell>

                    <TableCell className="text-right py-4 pr-6">
                      <div className="text-xs">
                        <div className="text-muted-foreground mb-0.5">Son güncelleme</div>
                        <div>{formatDate(announcement.updatedAt)}</div>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Sayfalama */}
        {filteredAnnouncements.length > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
            <div className="text-sm text-slate-600">
              <span className="font-semibold text-slate-800">{filteredAnnouncements.length}</span> anons içinden{" "}
              <span className="font-semibold text-slate-800">{startIndex + 1}</span>-
              <span className="font-semibold text-slate-800">{Math.min(endIndex, filteredAnnouncements.length)}</span>{" "}
              arası gösteriliyor
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="h-10 px-4 rounded-lg border-slate-200 hover:bg-slate-50 disabled:opacity-50"
                aria-label="Önceki sayfa"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                <span className="hidden sm:inline">Önceki</span>
              </Button>

              <div className="flex items-center gap-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                  // İlk sayfa, son sayfa veya current page etrafındaki 1 sayfa
                  if (
                    page === 1 ||
                    page === totalPages ||
                    (page >= currentPage - 1 && page <= currentPage + 1)
                  ) {
                    return (
                      <Button
                        key={page}
                        variant={currentPage === page ? "default" : "outline"}
                        size="sm"
                        onClick={() => handlePageChange(page)}
                        className={`h-10 min-w-10 px-3 rounded-lg ${
                          currentPage === page
                            ? "bg-blue-600 hover:bg-blue-700 text-white pointer-events-none"
                            : "border-slate-200 hover:bg-slate-50"
                        }`}
                      >
                        {page}
                      </Button>
                    );
                  }

                  // Boşlukları göster
                  if (page === 2 && totalPages > 4) {
                    return <Button key="start-ellipsis" variant="outline" size="sm" disabled className="h-10 px-2 rounded-lg border-slate-200 pointer-events-none">...</Button>;
                  }

                  if (page === totalPages - 1 && totalPages > 4) {
                    return <Button key="end-ellipsis" variant="outline" size="sm" disabled className="h-10 px-2 rounded-lg border-slate-200 pointer-events-none">...</Button>;
                  }

                  return null;
                })}
              </div>

              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="h-10 px-4 rounded-lg border-slate-200 hover:bg-slate-50 disabled:opacity-50"
                aria-label="Sonraki sayfa"
              >
                <span className="hidden sm:inline">Sonraki</span>
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
